import { call, put, select } from 'redux-saga/effects';
import {
  fetchViewRunDataRequest,
  saveReturnFailure,
  saveReturnSuccess,
  setUpdateFromPageRequest,
  updateActivityRoleRequest,
} from '../actions';
import { ApiError } from 'store/api/types';
import { SaveReturnRequestAction } from '../types';
import { checkResponse } from 'store/utils';

import { SaveReturnResponse, saveReturn, updateReturn } from '../api';
import { turnMessageOn } from 'store/app/actions';
import { MessageData } from 'store/app/types';
import { MessageStates } from 'containers/MessageBox';
import { updateSubmissionLog } from 'store/organisation/actions';
import {
  ActivityRoles,
  DataLogStatus,
  SubmissionUpdateField,
} from 'store/app/enums';
import { getAuthUser } from 'store/auth/selectors';
import { getUserMembers } from 'store/user/selectors';
import routes from 'routes';
import { isTifdReportPath } from 'utils/tifd-mapping';
import { saveTifdReportFromReturnSaga } from 'store/tifd/sagas/saveTifdReportSaga';
import { logoutRequest } from 'store/auth/actions';
import { renderErrorMessage } from 'utils/render-error-message';
import i18n from 'i18next';

/* eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types */
export function* saveReturnSaga(action: SaveReturnRequestAction) {
  const savedTifd: boolean = yield call(saveTifdReportFromReturnSaga, action);
  if (savedTifd) return;

  try {
    let err: ApiError | null;
    let response: SaveReturnResponse;
    const { saveReturnRequestData, reportId } = action.payload;
    const isFirstSave = !saveReturnRequestData.dataLogId;

    if (isFirstSave) {
      response = yield call(saveReturn, saveReturnRequestData, reportId);
    } else {
      response = yield call(updateReturn, saveReturnRequestData, reportId);
    }

    err = checkResponse(response?.responseMessage);
    if (err) throw err;

    const dataLogId = saveReturnRequestData.dataLogId
      ? saveReturnRequestData.dataLogId
      : response.data.dataLogId;
    const currentUser = yield select(getAuthUser);
    const users = yield select(getUserMembers);
    const generatedDate = saveReturnRequestData.generatedDate;

    // Keep updateFromProvider false so the page can reload report data after
    // fetchViewRunData (same path for first save and save-after-refresh).
    yield put(
      saveReturnSuccess(dataLogId, users, currentUser, generatedDate, false)
    );
    const message: MessageData = {
      title: i18n.t('common.success-exclamation'),
      description: response.responseMessage.message,
      type: MessageStates.SUCCESS,
    };

    if (isFirstSave) {
      const reportLink = isTifdReportPath(window.location.pathname)
        ? routes.vat.tifdViewReportLink
        : routes.vat.viewReportLink;
      window.history.pushState(
        {},
        '',
        reportLink
          .replace(':connectionId', saveReturnRequestData.connectionId)
          .replace(':mappingId', saveReturnRequestData.mappingId)
          .replace(':dataLogId', dataLogId)
      );
    }

    // Both first save and update-after-refresh reload run metadata; the page
    // then loadReports once updatingFromPage clears (mirrors first-save UX).
    yield put(setUpdateFromPageRequest(true));
    yield put(
      fetchViewRunDataRequest({
        connectionId: saveReturnRequestData.connectionId,
        mappingId: saveReturnRequestData.mappingId,
        dataLogId,
      })
    );

    yield put(turnMessageOn(message));
    yield put(
      updateActivityRoleRequest(
        {
          activityRole: ActivityRoles.PREPARER,
          memberUser: currentUser,
          dataLogId,
          mappingId: saveReturnRequestData.mappingId,
        },
        true
      )
    );

    yield put(
      updateSubmissionLog(
        saveReturnRequestData.mappingId,
        SubmissionUpdateField.STATUS,
        DataLogStatus.SAVED,
        dataLogId
      )
    );
  } catch (error: any) {
    const message: MessageData | null = renderErrorMessage(error);
    if (message) {
      yield put(turnMessageOn(message));
    } else {
      yield put(logoutRequest());
    }
    yield put(saveReturnFailure());
  }
}
